// Mirrors the JSON shapes returned by /api/admin/* — Date fields land as
// ISO strings over the wire, so we capture both the raw-string view and a
// helper that hydrates them for the UI.

export type Role = "student" | "admin";

export type UserStatsRow = {
  id: number;
  identifier: string;
  displayName: string;
  /** ISO timestamp string from the API. */
  createdAt: string;
  roles: Role[];
  sessionCount: number;
  finishedCount: number;
  /** ISO timestamp string or null. */
  lastSessionAt: string | null;
  averagePercentage: number | null;
  passRate: number | null;
  defaultModule: string | null;
};

export type AdminSessionRow = {
  id: string;
  mode: string;
  startedAt: string;
  finishedAt: string | null;
  totalScore: number | null;
  maxScore: number | null;
  percentage: number | null;
  passed: boolean | null;
};

export type AdminTrendPoint = {
  sessionId: string;
  startedAt: string;
  percentage: number | null;
  passed: boolean | null;
};

export type UserKpis = {
  sessionCount: number;
  finishedCount: number;
  averagePercentage: number | null;
  passRate: number | null;
  trendDelta: number | null;
};

export type AdminTurnRow = {
  turnId: string;
  sessionId: string;
  createdAt: string;
  part: number;
  promptText: string;
  userText: string;
  scoreTotal: number;
  scoreMax: number;
  audioPath: string | null;
  scores: { ad: number; wo: number; gr: number; vl: number; uit: number };
  feedbackMd: string;
  generatorProvider: string | null;
};

export type TurnsPage = {
  turns: AdminTurnRow[];
  page: number;
  pageSize: number;
  total: number;
};

export type AdminUserDetail = {
  user: {
    id: number;
    identifier: string;
    displayName: string;
    createdAt: string;
    roles: Role[];
  };
  trend: AdminTrendPoint[];
  sessions: AdminSessionRow[];
  turns: TurnsPage;
};

export type ProviderName = "Anthropic" | "Google" | "ElevenLabs";
export type ProviderIndicator = "ok" | "minor" | "major" | "critical" | "unknown";

export type ProviderStatus = {
  name: ProviderName;
  indicator: ProviderIndicator;
  description: string;
  detail?: string;
};

export type ElevenLabsUsage = {
  characterCount: number;
  characterLimit: number;
  resetAtUnix: number;
  tier: string;
  status: string;
};

export type ActiveIssueSeverity = "warn" | "critical";

export type ActiveIssue = {
  id: string;
  severity: ActiveIssueSeverity;
  title: string;
  description: string;
  lastSeenAt: number;
  count: number;
};

export type ProviderSpend = {
  provider: string;
  estimatedCostUsd: number;
  callCount: number;
  inputTokens: number;
  outputTokens: number;
};

export type CostsSnapshot = {
  monthlyByProvider: ProviderSpend[];
  budgets: { anthropic: number | null; google: number | null };
};

export type ServiceEvent = {
  at: number;
  kind: string;
  provider?: string;
  message: string;
};

export type HealthSnapshot = {
  fetchedAt: number;
  providers: ProviderStatus[];
  elevenlabs: ElevenLabsUsage | null;
  events: ServiceEvent[];
  activeIssues: ActiveIssue[];
  costs: CostsSnapshot;
};

export type CachePurgeResponse = {
  questionId: string;
  imagesDeleted: number;
  audioDeleted: boolean;
  imageUrlsCleared: boolean;
};

/** Helper — hydrate ISO strings sent by Hono.c.json() back into Dates. */
export function toDate(s: string | null): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}
